import { useCallback, useMemo } from 'react';
import {
  type IListItemData,
  fromEligibleCandidate,
} from '~/components/ShiftRequestRedesign/AddInvites/EmbeddedInviteList/professionalAdapters';
import { selectInvitedCandidateIds, useGetEligibleCandidates } from '~/domain/PermanentJobs';
import { useListSelection } from '~/hooks/useListSelection';

interface IUseCandidateSelectionArgs {
  postingId: string;
  /** Scopes the "Sent" state — a candidate is Sent when already invited to this interview. */
  interviewId: string;
  /** `YYYY-MM-DD` (office tz) — drives availability/exclusion/ordering on the backend. */
  date?: string;
  onSelectionChange: (ids: string[]) => void;
}

/**
 * Owns the Interview Details candidate-list state: fetches eligible candidates,
 * derives the invite rows and the non-selectable "Sent" set, and delegates the
 * selection bookkeeping (toggle, select-all, mirror-up) to `useListSelection`.
 * Kept as a hook so the component stays presentational and the logic is
 * unit-testable in isolation.
 */
export const useCandidateSelection = ({
  postingId,
  interviewId,
  date,
  onSelectionChange,
}: IUseCandidateSelectionArgs) => {
  const { data: candidates, isLoading } = useGetEligibleCandidates(
    { postingId, date },
    { enabled: Boolean(postingId) },
  );

  const rows = useMemo<IListItemData[]>(() => candidates.map(fromEligibleCandidate), [candidates]);

  // "Sent" for THIS interview — the backend refuses (silently) a second invite to
  // an interview the candidate is already on, so these rows must stay non-selectable.
  const sentIds = useMemo(
    () => new Set(selectInvitedCandidateIds(candidates, interviewId)),
    [candidates, interviewId],
  );

  const getId = useCallback((row: IListItemData) => row.id, []);
  // Only available, not-yet-invited candidates can be selected.
  const isSelectable = useCallback(
    (row: IListItemData) => row.isAvailable && !sentIds.has(row.id),
    [sentIds],
  );

  const { selected, selectableIds, allSelected, toggle, toggleAll } = useListSelection({
    items: rows,
    getId,
    isSelectable,
    onSelectionChange,
  });

  return {
    isLoading,
    rows,
    sentIds,
    selected,
    selectableIds,
    allSelected,
    toggleSelection: toggle,
    toggleSelectAll: toggleAll,
  };
};
